/**
 * `placement` — where a new or moved row lands, shared by groups (`specs/M1-B2-groups.md` §5.3)
 * and items (`specs/M1-B3-items.md` §5.3).
 *
 * monday expresses it as `position_relative_method: PositionRelative` (`before_at | after_at`)
 * plus `relative_to`; the UI also needs plain `top` / `bottom`. Both collapse onto one union so
 * the ordering maths lives in exactly one place.
 */
import { z } from 'zod';
import { needsRebalance, positionBetween } from './ordering.js';
/** monday's `PositionRelative` enum, verbatim. */
export const POSITION_RELATIVE_METHODS = ['before_at', 'after_at'];
export const placementSchema = z.union([
    z.literal('top'),
    z.literal('bottom'),
    z.object({
        method: z.enum(POSITION_RELATIVE_METHODS),
        /** A group id (string) or an item id (decimal string). */
        relativeTo: z.string().min(1),
    }),
]);
/**
 * Resolves `placement` against the **active** siblings, already sorted by position ascending.
 * Never throws for a missing gap: it reports `rebalance: true` and the index the row belongs
 * at, and the service renumbers with `positionSequence(n)` inside the same transaction.
 *
 * Throws only when `relativeTo` names a row that is not in the list — monday's `not_found`.
 */
export function resolvePlacement(placement, siblings) {
    const index = placementIndex(placement, siblings);
    const prev = index > 0 ? (siblings[index - 1]?.position ?? null) : null;
    const next = index < siblings.length ? (siblings[index]?.position ?? null) : null;
    if (needsRebalance(prev, next))
        return { position: prev ?? '', rebalance: true, index };
    try {
        return { position: positionBetween(prev, next), rebalance: false, index };
    }
    catch {
        // `positionBetween` also throws on underflow at the head of a full list.
        return { position: prev ?? '', rebalance: true, index };
    }
}
/** The 0-based slot the new row occupies among `siblings`. */
export function placementIndex(placement, siblings) {
    if (placement === 'top')
        return 0;
    if (placement === 'bottom')
        return siblings.length;
    const target = siblings.findIndex((row) => row.id === placement.relativeTo);
    if (target === -1) {
        throw new Error(`placement target "${placement.relativeTo}" is not an active sibling`);
    }
    return placement.method === 'before_at' ? target : target + 1;
}
/**
 * `create_group(position:)` / `update_group(group_attribute: position)` are monday's deprecated
 * absolute form: a fractional number as a string. It is interpreted as "insert so the numeric
 * order is respected" (`specs/M1-B2-groups.md` §7), which is a placement among the siblings.
 */
export function placementFromAbsolutePosition(value, siblings) {
    const target = Number(value);
    if (!Number.isFinite(target))
        return 'bottom';
    const after = [...siblings].reverse().find((row) => Number(row.position) < target);
    if (after)
        return { method: 'after_at', relativeTo: after.id };
    return siblings.length === 0 ? 'bottom' : 'top';
}
//# sourceMappingURL=placement.js.map